import React from 'react';
import PropTypes from 'prop-types';
import { ExternalLink } from 'lucide-react';
import Modal from 'tailwind-components/Modal/Modal';
import PdfViewer from './PdfViewer';
import { formatPeriod } from '../helpers/callReviewsDates';

// the agent and the period are what identify a report to a reader; the stored
// file name is machine-generated and embeds an email, so it is never shown
function previewTitle(file, agentName) {
  if (!file) return '';

  const period = formatPeriod(file.from_date, file.to_date);
  if (!agentName) return period;
  return `${agentName} — ${period}`;
}

/**
 * pdf preview for one report. rendered as a sibling of the tables and driven by
 * `file`, so opening or closing it never unmounts a table and page and expansion
 * state survive.
 * @param file - the AnalysisFileItem to preview, or null when closed
 * @param agentName - the agent the report belongs to, shown in the title
 * @param onClose - called to dismiss the preview
 */
export default function FilePreviewModal({ file, agentName, onClose }) {
  const title = previewTitle(file, agentName);

  return (
    <Modal
      active={Boolean(file)}
      closeModal={onClose}
      title={title}
      size='xlarger'
      hideFooter
    >
      {file && (
        <div className='flex flex-col gap-2'>
          {/* always available, in case the browser refuses to render the pdf inline */}
          <a
            href={file.object_url}
            target='_blank'
            rel='noreferrer'
            className='flex items-center gap-1.5 self-end text-[length:--font-size-body] text-primary-darkened underline'
          >
            <ExternalLink className='size-4' aria-hidden />
            Open in new tab
          </a>
          <PdfViewer url={file.object_url} title={title} />
        </div>
      )}
    </Modal>
  );
}

FilePreviewModal.propTypes = {
  file: PropTypes.object,
  agentName: PropTypes.string,
  onClose: PropTypes.func.isRequired
};
